import styled, { css } from 'styled-components';
import defaultTheme from 'src/utils/theme';
import { Icon, Popout as PopoutComponent, Stack } from 'src/components';
import { GrowthLevelAndToggles as _GrowthLevelAndToggles } from 'src/apps/LearningSession/Common/GrowthLevelAndToggles';

export const ContentLayer = styled.div<{ $fillViewport?: boolean }>(
  ({ theme, $fillViewport }) => css`
    position: relative;
    z-index: ${theme.zi.z1};
    width: 100%;
    box-sizing: border-box;

    ${
      $fillViewport &&
      css`
        display: flex;
        flex-direction: column;
        flex: 1;
        height: 100%;
        min-height: 100%;
        background-color: #000;
        pointer-events: none;

        * {
          pointer-events: none;
        }

        button,
        a,
        input,
        textarea,
        select,
        label,
        [role='button'],
        [role='dialog'],
        [data-testid='presentationInfoContainer'],
        [data-testid='learning-tools-panel'],
        [data-testid='mobile-learning-tools-slot'],
        [data-testid='mobile-learning-tools-slot'] *,
        /* Side-control wrappers stretch full height — only their interactive
           children (covered by the button rule above) should capture clicks,
           or they eat the right edge of MC options docked under them. */
        [data-testid='presentation-video-controls'],
        [data-testid='presentation-video-controls'] *,
        [data-testid='video-test-footer'] *,
        [data-testid='video-test-options'],
        [data-testid='video-test-options'] * {
          pointer-events: auto;
        }

        @media (max-width: ${theme.sz.md}) {
          min-height: 100%;
        }
      `
    }
  `,
);

export const ProgressBarWrapper = styled(Stack)<{ $overlay?: boolean }>(
  ({ theme, $overlay }) => css`
    ${
      $overlay
        ? css`
            position: relative;
            z-index: ${theme.zi.z2};
            width: 100%;
            box-sizing: border-box;
            flex-shrink: 0;
            padding: ${theme.sz.s3} 0 ${theme.sz.s4};

            @media (min-width: ${theme.sz.ml}) {
              padding: ${theme.sz.s4} 0 ${theme.sz.s5};
            }
          `
        : css`
            padding-bottom: ${theme.sz.s5};
          `
    }
  `,
);

export const ButtonWrapper = styled.div<{
  $isMobile: boolean;
  $fullWidth?: boolean;
}>(
  ({ theme, $isMobile, $fullWidth }) => css`
    margin-bottom: ${$isMobile ? theme.sz.s4 : theme.sz.s6};
    display: grid;
    width: ${$fullWidth && '100%'};
  `,
);

export const NextButtons = styled.div<{ $fixedWidth?: boolean; $docked?: boolean }>(
  ({ theme, $fixedWidth, $docked }) => css`
    width: ${$fixedWidth || $docked ? '280px' : undefined};
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: ${$fixedWidth || $docked ? 'stretch' : 'center'};
    gap: ${$docked ? theme.sz.s3 : undefined};
    box-sizing: border-box;

    ${
      $docked &&
      css`
        @media (max-width: ${theme.sz.md}) {
          width: 100%;
          gap: ${theme.sz.s3};

          /* Match Continue / IAKT / video-controls width, height, and spacing. */
          > [data-testid='presentation-video-controls'],
          > ${ButtonWrapper} {
            width: 100%;
            margin: 0;
            box-sizing: border-box;
            min-height: ${theme.sz.s14};
          }

          > ${ButtonWrapper} > * {
            min-height: ${theme.sz.s14};
          }
        }

        @media (min-width: ${theme.sz.md}) {
          > [data-testid='presentation-video-controls'],
          > ${ButtonWrapper} {
            width: 100%;
            margin-bottom: 0;
            box-sizing: border-box;
          }
        }
      `
    }
  `,
);

const overlayPanelStyles = ({ theme }: { theme: typeof defaultTheme }) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.sz.s3};
  margin-bottom: ${theme.sz.s4};
  width: 100%;
  padding: ${theme.sz.s4};
  border-radius: ${theme.bo.medium};
  background: linear-gradient(
    160deg,
    rgba(0, 0, 0, 0.62) 0%,
    rgba(0, 0, 0, 0.42) 55%,
    rgba(0, 0, 0, 0.28) 100%
  );

  @media (min-width: ${theme.sz.md}) {
    margin-bottom: ${theme.sz.s6};
  }
`;

export const VideoControls = styled.div(
  ({ theme }) => css`
    ${overlayPanelStyles({ theme })}
    margin-bottom: 0;
    box-sizing: border-box;

    @media (max-width: ${theme.sz.md}) {
      flex-direction: row;
      align-items: center;
      justify-content: center;
      min-height: ${theme.sz.s14};
      padding: ${theme.sz.s2} ${theme.sz.s4};
    }
  `,
);

export const MainColumn = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    gap: ${theme.sz.s4};

    @media (min-width: ${theme.sz.md}) {
      gap: ${theme.sz.s6};
    }
  `,
);

export const SessionRow = styled.div<{ $fillViewport?: boolean }>(
  ({ theme, $fillViewport }) => css`
    display: flex;
    flex-direction: row;
    width: 100%;
    box-sizing: border-box;
    gap: ${theme.sz.s4};

    ${
      $fillViewport &&
      css`
        position: relative;
        flex: 1;
        min-height: 0;
        align-items: stretch;

        /* Leave the first screen for the speaker + dock; learning tools scroll below. */
        @media (max-width: ${theme.sz.md}) {
          flex: 1 1 auto;
          min-height: 0;
        }
      `
    }
  `,
);

export const LearningToolsPanel = styled.section(
  ({ theme }) => css`
    ${overlayPanelStyles({ theme })}
    margin-top: 0;
    width: 100%;
    max-width: ${theme.sz.xs};
    max-height: 55vh;
    overflow-y: auto;

    [data-testid='example-sentences'],
    [data-testid='mem-gallery'] {
      margin-top: 0;
    }

    [data-testid='nugget-button'] {
      width: 100%;
      max-width: 100%;
    }

    @media (max-width: ${theme.sz.md}) {
      max-width: none;
      max-height: none;
    }
  `,
);

export const LearningToolsHeading = styled.h2(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xxs};
    color: ${theme.co.neutral99};
    margin: 0;
  `,
);

export const VideoControlsRow = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: ${theme.sz.s2};

    /* Keep all control icons at full contrast (inactive toggles default to muted grey). */
    button {
      color: ${theme.co.secondary0};
    }

    [data-testid='videoSpeedValueLabel'] {
      color: ${theme.co.neutral90};
    }
  `,
);

export const ActorFaceButton = styled.button(
  ({ theme }) => css`
    flex-shrink: 0;
    margin: 0;
    padding: 0;
    border: none;
    border-radius: ${theme.bo.round};
    background: none;
    cursor: pointer;
  `,
);

export const VideoControlButton = styled.button(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.sz.s10};
    height: ${theme.sz.s10};
    margin: 0;
    padding: 0;
    border: ${theme.bo.thinWidth} solid ${theme.co.neutral30};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.neutral99};
    color: ${theme.co.secondary0};
    cursor: pointer;

    &:hover {
      border-color: ${theme.co.secondary0};
    }

    & svg {
      font-size: ${theme.sz.s6};
    }
  `,
);

export const ButtonsSideWrapper = styled.div<{
  $isCentered: boolean;
  $dockBottom?: boolean;
  /** Float over the right edge without taking flex width (video MC tests). */
  $dockEnd?: boolean;
}>(
  ({ theme, $isCentered, $dockBottom, $dockEnd }) => css`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: ${theme.sz.s4};
    align-items: ${$isCentered ? 'center' : 'stretch'};
    box-sizing: border-box;
    position: relative;
    z-index: ${theme.zi.z2};
    /* Let clicks pass through empty column space onto MC options underneath. */
    pointer-events: none;

    button,
    a,
    [role='button'] {
      pointer-events: auto;
    }

    ${
      $dockEnd &&
      css`
        @media (min-width: ${theme.sz.md}) {
          position: absolute;
          top: 0;
          right: 0;
          align-items: flex-end;
          width: auto;
          max-width: 100%;
        }

        @media (max-width: ${theme.sz.md}) {
          align-self: stretch;
          align-items: stretch;
          width: 100%;
        }
      `
    }

    ${
      $dockBottom &&
      css`
        /* Desktop: pin to the padded content box. Mobile: stay in flow so CTAs
           don't overlap Learning Tools below the fold. */
        @media (min-width: ${theme.sz.md}) {
          position: absolute;
          right: 0;
          bottom: ${theme.sz.s4};
          z-index: ${theme.zi.z2};
          width: min(280px, 100%);
        }

        @media (min-width: ${theme.sz.ml}) {
          bottom: ${theme.sz.s8};
        }

        @media (max-width: ${theme.sz.md}) {
          position: relative;
          width: 100%;
          z-index: ${theme.zi.z2};
          gap: ${theme.sz.s3};
        }
      `
    }
  `,
);

/** First mobile screen: learnable + CTA stack. Learning tools render after this. */
export const MobileAboveFold = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    width: 100%;
    box-sizing: border-box;
    gap: ${theme.sz.s3};
    /* Leave room for the session header and bottom safe area so Continue is fully
       visible above the fold, with Learning Tools only after scroll. */
    min-height: calc(100dvh - ${theme.sz.s12} - ${theme.sz.s8} - env(safe-area-inset-bottom, 0px));
    padding-bottom: max(${theme.sz.s4}, env(safe-area-inset-bottom, 0px));

    > [data-testid='presentation-side-controls'] {
      margin-top: auto;
    }
  `,
);

export const MobileLearningToolsSlot = styled.div(
  ({ theme }) => css`
    width: 100%;
    box-sizing: border-box;
    margin-top: ${theme.sz.s8};
    padding-bottom: ${theme.sz.s4};
    pointer-events: auto;

    [data-testid='learning-tools-panel'] {
      margin-top: 0;
    }
  `,
);

export const ButtonWrapperFooter = styled.div<{
  $hasStickyButton: boolean;
  $hasOneRow: boolean;
  $transparent?: boolean;
}>(
  ({ theme, $hasStickyButton, $hasOneRow, $transparent }) => css`
    position: ${$hasStickyButton && 'sticky'};
    z-index: ${theme.zi.z2};
    bottom: ${theme.sz.s0};
    width: 100%;
    padding-top: ${theme.sz.s4};
    background: ${$transparent ? 'transparent' : theme.co.neutral99};
    display: grid;
    grid-gap: ${theme.sz.s3};
    grid-template-columns: ${$hasOneRow ? 'auto' : `${theme.sz.s12} auto`};

    align-items: ${$hasOneRow ? 'center' : 'end'};
  `,
);

export const MobileNuggetWrapper = styled.div(
  ({ theme }) => css`
    width: 100%;
    margin-top: ${theme.sz.s4};
    margin-bottom: ${theme.sz.s4};
    box-sizing: border-box;

    & > * {
      width: 100%;
      max-width: 100%;
    }
  `,
);

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr ${defaultTheme.sz.s12} ${defaultTheme.sz.s32};
  grid-column-gap: ${defaultTheme.sz.s2};
  grid-row-gap: ${defaultTheme.sz.s4};

  @media (min-width: ${defaultTheme.sz.md}) {
    grid-column-gap: ${defaultTheme.sz.s6};
    grid-template-columns: 1fr 5.25rem;
    grid-template-rows: max-content max-content 1fr;
    grid-row-gap: ${defaultTheme.sz.s8};
  }
`;

export const ContentWrapper = styled.div`
  grid-column: 1 / 4;

  @media (min-width: ${defaultTheme.sz.md}) {
    grid-column: 1 / 2;
    grid-row: 2 / 4;
  }
`;

export const GrowthLevelAndToggles = styled(_GrowthLevelAndToggles)`
  grid-column: 3;
  grid-row: 1;

  @media (min-width: ${defaultTheme.sz.md}) {
    grid-row: 3;
    grid-column: 2;
  }
`;

export const BuddyButton = styled.a(
  ({ theme }) => css`
    background: none;
    border: none;
    cursor: pointer;
    border-radius: ${theme.bo.round};

    &:hover,
    &:focus-visible {
      // global focus-visible not suitable here
      box-shadow: none;
      svg {
        transform: scale(1.1);
        box-shadow: ${theme.sh.s4};
        border-radius: 50%;
      }
    }
  `,
);

export const BuddySpeechIcon = styled(Icon)(
  ({ theme }) => css`
    font-size: ${theme.sz.s18};

    @media (max-width: ${theme.sz.md}) {
      font-size: ${theme.sz.s14};
    }
  `,
);

export const Popout = styled(PopoutComponent)(
  () => css`
    width: max-content;
    max-width: max-content;
  `,
);

export const BuddyButtonWrapper = styled.div(
  ({ theme }) => css`
    margin-bottom: ${theme.sz.s3};
  `,
);

export const Bold = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xxs};
  `,
);
